import { z } from 'zod'
import {
  CommonBlocksCompoundIndexes,
  isListBlock,
  normalizeStringPath,
  Block,
  isBlockWithChildren,
  isCommentBlock,
  isComponentBlock,
  isDocumentBlock,
  isInstanceBlock,
  isReferenceBlock,
  blockWithPathSchema,
  BlockWithPath,
  getVariantsDataFromKey,
  isAgentBlock,
} from '@basehub/data/replicache/index'
import { TreeManagerMethods } from '@basehub/utils/tree/index'
import { invariant } from '@epic-web/invariant'
import { Replicache } from 'replicache'
import { repoSpaceMutators } from '@basehub/data/replicache/mutators/repo-space/mutators'
import { isEmpty } from '@basehub/tiptap-api-utils'
import type { Tab } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { toast } from 'sonner'
import difference from 'lodash.difference'
import { getId } from '@basehub/utils/id/index'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { isErrorLike } from '@basehub/utils/error'
import { RepoStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { RepoUndoOps } from '~/replicache/frontend/undo-redo/manager'
import { PasteBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/paste'

export async function copyBlockToClipboard({
  onSuccess,
  onError,
  blockId,
  blockTreeManager,
}: {
  blockId: string
  blockTreeManager: TreeManagerMethods<Block, NonNullable<unknown>>
  onSuccess?: VoidFunction
  onError?: (err: unknown) => void
}) {
  try {
    const nodeToCopy = blockTreeManager.getNode(blockId, {
      fallbackKey: 'sourceBlockId',
      required: true,
    })
    invariant(nodeToCopy.parentKey, 'Block has no parent')

    const dependencies = new Set<string>()
    const blocksToCopy = getBlockWithChildrenData(nodeToCopy.value, {
      treeManager: blockTreeManager,
      dependencies,
    })
    for (const block of blocksToCopy) {
      if (block.type === 'instance' || block.type === 'component') {
        dependencies.delete(block.id)
      }
    }

    const supportBlocksData = getSupportBlocksData(
      dependencies,
      blockTreeManager
    )

    const htmlEncoded = `<span data-bshb-root-id="${
      blockTreeManager.root.key
    }" data-bshb-blocks="${jsonToDataAttr(
      blocksToCopy
    )}" /><span data-bshb-support-data="${jsonToDataAttr(
      supportBlocksData
    )}" />`
    const data = [
      new ClipboardItem({
        'text/html': new Blob([htmlEncoded], { type: 'text/html' }),
      }),
    ]
    await navigator.clipboard.write(data)
    onSuccess && onSuccess()
  } catch (err) {
    onError && onError(err)
  }
}

async function getBlocksDataFromClipboard() {
  try {
    const clipboardData = (await navigator.clipboard.read())[0]

    if (!clipboardData?.types.includes('text/html')) {
      // The clipboard data does not have any bshb blocks data
      return null
    }
    const htmlBlob = await clipboardData?.getType('text/html')
    if (!htmlBlob) return
    const tempElement = document.createElement('div')
    tempElement.innerHTML = await htmlBlob.text()

    const rawBlocksData = tempElement
      .querySelector('span[data-bshb-blocks]')
      ?.getAttribute('data-bshb-blocks')
    const rawSupportData = tempElement
      .querySelector('span[data-bshb-support-data]')
      ?.getAttribute('data-bshb-support-data')
    const rootId = tempElement
      .querySelector('span[data-bshb-root-id]')
      ?.getAttribute('data-bshb-root-id')
    if (!rawBlocksData) return

    const blocksData = z
      .array(blockWithPathSchema)
      .nonempty()
      .parse(JSON.parse(Buffer.from(rawBlocksData, 'base64').toString('utf-8')))
    const supportData = z
      .array(blockWithPathSchema)
      .parse(
        rawSupportData
          ? JSON.parse(Buffer.from(rawSupportData, 'base64').toString('utf-8'))
          : []
      )

    return { blocksData, supportData, rootId: rootId ?? '' }
  } catch (e) {
    console.error(e)
    toast.error('An error occurred while reading the clipboard')
  }
}

const getBlockWithChildrenData = (
  block: Block,
  {
    treeManager,
    dependencies,
    relativePath = [],
  }: {
    treeManager: TreeManagerMethods<Block, CommonBlocksCompoundIndexes>
    dependencies: Set<string>
    relativePath?: string[]
  }
): BlockWithPath[] => {
  if (
    'variantOverrides' in block &&
    block.variantOverrides &&
    'acceptedVariantSets' in block.constraints &&
    block.constraints.acceptedVariantSets
  ) {
    for (const key of Object.keys(block.variantOverrides)) {
      const data = getVariantsDataFromKey(key)
      for (const variant of data) {
        if (block.constraints.acceptedVariantSets.includes(variant.setId)) {
          dependencies.add(variant.setId)
        }
      }
    }
  }

  switch (block.type) {
    case 'instance':
    case 'list': {
      dependencies.add(block.typeOptions.targetBlockId)
      break
    }
    case 'rich-text':
      for (const opt of block.constraints.allowedComponents ?? []) {
        dependencies.add(opt)
      }
      break
    case 'reference':
      if (!block.constraints.acceptedTypes) break
      for (const opt of block.constraints.acceptedTypes) {
        dependencies.add(opt)
      }
      for (const opt of block.value.targetBlockIds ?? []) {
        dependencies.add(opt)
      }
      break
  }

  const children = isBlockWithChildren(block)
    ? treeManager
        .getChildrenKeys(idToSourceBlockId(block))
        .map((k) => treeManager.getNode(k, { required: true }).value)
    : null

  const acc: BlockWithPath[] = [
    {
      ...block,
      relativePath,
      relationships: children?.map((b) => ({
        blockId: b.id,
        sourceBlockId: b.sourceBlockId ?? b.id,
      })),
    },
  ]
  if (!children?.length) return acc

  for (const child of children) {
    acc.push(
      ...getBlockWithChildrenData(child, {
        treeManager,
        dependencies,
        relativePath: [...relativePath, block.id],
      })
    )
  }

  return acc
}

const getSupportBlocksData = (
  idsToLookFor: Set<string>,
  treeManager: TreeManagerMethods<Block, CommonBlocksCompoundIndexes>
): Block[] => {
  const checked = new Set<string>()
  function getBlockAndChildren(
    blockIds: string[],
    relativePath: string[] = []
  ) {
    const blocks: BlockWithPath[] = []
    for (const id of blockIds) {
      if (checked.has(id)) continue
      const block = treeManager.getNode(id, {
        fallbackKey: 'sourceBlockId',
      })?.value

      if (!block) {
        console.warn(`Dependency with id ${id} not found`)
        checked.add(id)
        continue
      }

      const blockWithPath: BlockWithPath = { ...block, relativePath }

      checked.add(blockWithPath.id)
      blockWithPath.sourceBlockId && checked.add(blockWithPath.sourceBlockId)

      if (isBlockWithChildren(blockWithPath)) {
        const keys = treeManager.getChildrenKeys(
          idToSourceBlockId(blockWithPath)
        )

        if (keys.length) {
          blockWithPath.relationships = keys.map((childKey) => {
            const b = treeManager.getNode(childKey, { required: true }).value
            return {
              blockId: b.id,
              sourceBlockId: b.sourceBlockId ?? b.id,
            }
          })

          blocks.push(
            ...getBlockAndChildren(keys, [...relativePath, blockWithPath.id])
          )
        }
      }

      if (
        'variantOverrides' in blockWithPath &&
        blockWithPath.variantOverrides &&
        'acceptedVariantSets' in blockWithPath.constraints &&
        blockWithPath.constraints.acceptedVariantSets
      ) {
        for (const key of Object.keys(blockWithPath.variantOverrides)) {
          const data = getVariantsDataFromKey(key)
          for (const variant of data) {
            if (
              blockWithPath.constraints.acceptedVariantSets.includes(
                variant.setId
              )
            ) {
              blocks.push(...getBlockAndChildren([variant.setId]))
            }
          }
        }
      }

      switch (blockWithPath.type) {
        case 'rich-text':
          if (!blockWithPath.constraints.allowedComponents?.length) break
          blocks.push(
            ...getBlockAndChildren(blockWithPath.constraints.allowedComponents)
          )
          break
        case 'reference':
          if (!blockWithPath.constraints.acceptedTypes?.length) break
          blocks.push(
            ...getBlockAndChildren(blockWithPath.constraints.acceptedTypes)
          )
          blockWithPath.value.targetBlockIds?.length &&
            blocks.push(
              ...getBlockAndChildren(blockWithPath.value.targetBlockIds)
            )
          break
        case 'list':
        case 'instance':
          if (!blockWithPath.typeOptions.targetBlockId) break
          blocks.push(
            ...getBlockAndChildren([blockWithPath.typeOptions.targetBlockId])
          )
          break
      }
      blocks.push(blockWithPath)
    }
    return blocks
  }

  return getBlockAndChildren(Array.from(idsToLookFor)).flat()
}

function jsonToDataAttr(jsonValidData: Array<unknown>) {
  return Buffer.from(JSON.stringify(jsonValidData), 'utf8').toString('base64')
}

// mutations

type AddTab = (
  tab: Tab,
  options?: Partial<{
    shouldResetFollowStatus: boolean
    position: 'last' | 'next-to-active' | 'first'
  }>
) => void

async function handlePasteCopiedBlocks({
  repoId,
  selectedBlock,
  selectedBlockPath,
  replicache,
  copiedBlocksData,
  isPastingInADocumentLikeBlock,
  rootId,
  supportBlocksData,
  path,
  addTab,
  shiftKeyPressed,
  editModeIsEnabled = false,
}: {
  isPastingInADocumentLikeBlock: boolean
  repoId: string
  selectedBlock: Block | null | undefined
  selectedBlockPath: string | null | undefined
  replicache: Replicache<typeof repoSpaceMutators>
  copiedBlocksData: [BlockWithPath, ...BlockWithPath[]]
  rootId: string
  supportBlocksData: Block[]
  path: string
  addTab: AddTab
  shiftKeyPressed: boolean
  editModeIsEnabled: boolean | undefined
}) {
  const selectedBlockIsACommentWithNoContent = selectedBlock
    ? isCommentBlock(selectedBlock) && isEmpty(selectedBlock.value.state)
    : false

  try {
    if (rootId === path && !isPastingInADocumentLikeBlock) {
      // we are pasting in the root block
      for (const block of copiedBlocksData) {
        if (block.relativePath.length) continue
        invariant(
          isBlockWithChildren(block) || isAgentBlock(block),
          'cannot paste primitive blocks in the root block'
        )
      }
    }

    const params: PasteBlockParams = {
      type: 'copy',
      pastedBlocksData: copiedBlocksData,
      supportBlocksData,
      rootId,
      position:
        selectedBlock?.id && !isPastingInADocumentLikeBlock
          ? { type: 'after', targetId: selectedBlock.id }
          : { type: 'last' },
      path: (isPastingInADocumentLikeBlock ? selectedBlockPath : path) ?? path,
      selectedBlock:
        selectedBlock && selectedBlockPath
          ? {
              id: selectedBlock.id,
              type: selectedBlock.type,
              path: selectedBlockPath,
              instanceChildTarget: selectedBlock.instanceChildTarget,
            }
          : undefined,
      isoNow: new Date().toISOString(),
      duplicatedComponentStrategy: 'create-instance',
      idSeed: getId(),
      repoId,
      ...(selectedBlockIsACommentWithNoContent && {
        replaceBlockId: selectedBlock?.id ?? undefined,
      }),
      shiftKeyPressed,
      replaceValueOnly: editModeIsEnabled,
    }
    const pasteBlockResult = await replicache.mutate.pasteBlock(params)

    if (isPastingInADocumentLikeBlock && selectedBlock) {
      addTab({ key: selectedBlock.id, partiallyOpen: true })
    }

    return {
      ...pasteBlockResult,
      redo: { op: 'pasteBlock', args: params } satisfies RepoUndoOps[number],
    }
  } catch (error) {
    console.error(error)
    isErrorLike(error)
      ? toast.error(`Could not paste block: ${error.message}`)
      : toast.error('Could not paste block in the selected target')
  }
}

async function handlePasteCutBlocks({
  selectedBlock,
  blockTreeManager,
  path,
  replicache,
  repoStore,
  addTab,
  shiftKeyPressed,
  repoId,
  editModeIsEnabled = false,
  isPastingInADocumentLikeBlock,
}: {
  selectedBlock: Block | null
  blockTreeManager: TreeManagerMethods<Block, CommonBlocksCompoundIndexes>
  path: string
  repoId: string
  replicache: Replicache<typeof repoSpaceMutators>
  repoStore: Pick<RepoStore, 'cutBlocks' | 'setCutBlocks'>
  addTab: AddTab
  shiftKeyPressed: boolean
  editModeIsEnabled: boolean | undefined
  isPastingInADocumentLikeBlock: boolean
}) {
  const pathAsArray = normalizeStringPath({
    path: path ?? '',
    format: 'array',
    includeEdge: true,
  })
  const containerBlockId = pathAsArray.at(-1)

  if (!repoStore.cutBlocks?.ids[0]) return
  const [blockId] = repoStore.cutBlocks.ids

  if (blockId === selectedBlock?.id) {
    repoStore.setCutBlocks(null)
    return
  }

  // prevent pasting in instances, show a toast
  if (selectedBlock && isInstanceBlock(selectedBlock)) {
    toast.error('Pasting in an instance is not allowed')
    return
  }

  const [rootBlockId] = pathAsArray
  invariant(
    rootBlockId,
    `Root block id for block ${containerBlockId} not found`
  )

  invariant(
    containerBlockId,
    `could not retrieve parentBlockId from path: ${pathAsArray.toString()}`
  )
  const targetBlockId =
    selectedBlock &&
    (isDocumentBlock(selectedBlock) || isComponentBlock(selectedBlock))
      ? selectedBlock.id
      : containerBlockId

  const parentBlock = blockTreeManager?.getNode(containerBlockId, {
    fallbackKey: 'sourceBlockId',
    required: true,
  })?.value
  invariant(
    isBlockWithChildren(parentBlock),
    `targeted block with id ${containerBlockId} is not a parent block`
  )

  const parentBlockChildrenKeys = blockTreeManager.getChildrenKeys(
    idToSourceBlockId(parentBlock)
  )
  const tryToFindSelectedBlockIdInTheTargetBlock = selectedBlock
    ? parentBlockChildrenKeys.findIndex(
        (id) => selectedBlock.id === idToSourceBlockId(id, blockTreeManager)
      )
    : -1
  const intendedIndex =
    tryToFindSelectedBlockIdInTheTargetBlock >= 0
      ? tryToFindSelectedBlockIdInTheTargetBlock
      : parentBlockChildrenKeys.length
  const selectedBlockIsACommentWithNoContent = selectedBlock
    ? isCommentBlock(selectedBlock) && isEmpty(selectedBlock.value.state)
    : false

  const cuttedBlock = blockTreeManager.getNode(blockId, {
    fallbackKey: 'sourceBlockId',
  })?.value

  if (
    cuttedBlock &&
    isInstanceBlock(cuttedBlock) &&
    selectedBlock?.id === cuttedBlock.typeOptions.targetBlockId
  ) {
    toast.error('Can not paste instances in its template component')
    return
  }

  try {
    const params: PasteBlockParams = {
      type: 'cut',
      rootId: rootBlockId,
      blockIds: repoStore.cutBlocks.ids,
      targetBlockId,
      intendedIndex,
      path:
        isPastingInADocumentLikeBlock && selectedBlock
          ? path + '/' + selectedBlock.id
          : path,
      isoNow: new Date().toISOString(),
      idSeed: getId(),
      repoId,
      ...(selectedBlockIsACommentWithNoContent && {
        replaceBlockId: selectedBlock?.id ?? undefined,
      }),
      shiftKeyPressed,
      replaceValueOnly: editModeIsEnabled,
    }
    const pasteBlockResult = await replicache.mutate.pasteBlock(params)

    addTab({
      key: rootBlockId !== targetBlockId ? targetBlockId : blockId,
      partiallyOpen: true,
    })

    repoStore.setCutBlocks(null)

    return {
      ...pasteBlockResult,
      redo: { op: 'pasteBlock', args: params } satisfies RepoUndoOps[number],
    }
  } catch (error) {
    toast.error('Could not paste block in the selected target')
  }
}

// wrapper function
export async function pasteBlocksFromClipboard({
  repoId,
  replicache,
  destinationPath,
  blockTreeManager,
  editModeIsEnabled,
  repoStore,
  addTab,
  selectedBlock,
  shiftKeyPressed,
  enableAgentPaste = false,
}: {
  repoId: string
  replicache: Replicache<typeof repoSpaceMutators>
  addTab: AddTab
  destinationPath: string
  blockTreeManager: TreeManagerMethods<Block, CommonBlocksCompoundIndexes>
  selectedBlock: Block | null
  editModeIsEnabled: boolean
  repoStore: Pick<RepoStore, 'cutBlocks' | 'setCutBlocks'>
  shiftKeyPressed: boolean
  enableAgentPaste?: boolean
}) {
  const selectedBlockPath = selectedBlock?.id
    ? blockTreeManager.getPath(selectedBlock.id)
    : null

  const clipboardData = await getBlocksDataFromClipboard()
  if (!clipboardData) return

  if (blockTreeManager.root.key === destinationPath && !selectedBlock) {
    // we are pasting in the root block
    for (const block of clipboardData.blocksData) {
      if (enableAgentPaste && block.type === 'agent') continue
      if (block.relativePath.length || isBlockWithChildren(block)) continue
      if (block.type === 'agent') {
        toast.error(
          "Couldn't paste block: cannot paste agents in the Explore tab, go to the Agents tab to paste them"
        )
        return
      }
      toast.error(
        "Couldn't paste block: cannot paste primitive blocks in the root block"
      )
      return
    }
  }

  const isPastingInADocumentLikeBlock =
    !!selectedBlock &&
    (isComponentBlock(selectedBlock) ||
      isDocumentBlock(selectedBlock) ||
      isInstanceBlock(selectedBlock) ||
      isListBlock(selectedBlock) ||
      isReferenceBlock(selectedBlock))

  if (
    repoStore.cutBlocks?.ids.length &&
    // all cutBlocks are in the clipboard
    difference(
      repoStore.cutBlocks.ids,
      clipboardData.blocksData.map((b) => b.id)
    ).length === 0
  ) {
    const result = await handlePasteCutBlocks({
      selectedBlock,
      blockTreeManager,
      path: destinationPath,
      replicache,
      repoStore,
      addTab,
      shiftKeyPressed,
      repoId,
      editModeIsEnabled,
      isPastingInADocumentLikeBlock,
    })

    return result
  } else {
    if (repoStore.cutBlocks) {
      repoStore.setCutBlocks(null)
    }

    // prevent pasting in instances, show a toast
    if (isPastingInADocumentLikeBlock && isInstanceBlock(selectedBlock)) {
      toast.error('Pasting in an instance is not allowed')
      return
    }

    // If cutBlocks is not set or doesn't match the clipboard data, we do a normal copy and paste
    const result = await handlePasteCopiedBlocks({
      repoId,
      selectedBlock,
      selectedBlockPath,
      replicache,
      isPastingInADocumentLikeBlock,
      copiedBlocksData: clipboardData.blocksData,
      supportBlocksData: clipboardData.supportData,
      path: destinationPath,
      addTab,
      rootId: blockTreeManager?.root.key,
      shiftKeyPressed,
      editModeIsEnabled,
    })

    return result
  }
}
